import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  DeleteUsersRequest,
  InviteBulkUsersResponse,
  InviteUsersRequest,
  UpdateBulkUsersResponse,
  UpdateUserRequest,
} from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { UserWithRolesResponse } from 'projects/shared/services/inspection-companies/models/users/user-with-roles.response';
import { ReinvitePendingUsersModel } from 'projects/shared/services/invitations/models/reinvite-pending-users.model';
import { PaginatedResponse } from 'projects/shared/services/models/paginated.response';
import { FilterCriteria } from './FilterCriteria';
import { UsersDeletedResponse } from 'projects/shared/services/inspection-companies/models/users/users-deleted-response';
import { RoleResponse } from 'projects/shared/services/roles/models/role.response';

@Component({
  selector: 'shared-list-users',
  standalone: false,
  template: ` <div class="flex flex-col gap-2rem">
    <shared-list-users-toolbar
      [filterCriteria$]="filterCriteria$"
      [roles$]="roles$"
      [selectedUsers]="selectedUsers"
      [inviteUsersAction]="inviteUsersAction"
      [reinvitePendingUsersAction]="reinvitePendingUsersAction"
      (userInvited)="onUserInvited()"
      (usersReinvited)="onUsersReinvited()"
      [deleteUsersAction]="deleteUsersAction"
      (usersDeleted)="onUsersDeleted($event)"
      [bulkInviteUsersAction]="bulkInviteUsersAction"
      [bulkUpdateUsersAction]="bulkUpdateUsersAction"
      (bulkUsersUpdated)="onBulkUsersUpdated()" />
    <shared-list-users-table
      [filterCriteria$]="filterCriteria$"
      [roles$]="roles$"
      [paginatedData]="paginatedUsersResponse"
      [isLoading]="isLoadingUsers"
      [selectedUsers]="selectedUsers"
      (checkUser)="onUserChecked($event)"
      (userUpdated)="onUserUpdated()"
      (userDeleted)="onUserDeleted()"
      [updateUserAction]="updateUserAction"
      [deleteUsersAction]="deleteUsersAction" />
  </div>`,
})
export class ListUsersComponent implements OnInit {
  paginatedUsersResponse?: PaginatedResponse<UserWithRolesResponse>;
  selectedUsers = new Set<UserWithRolesResponse>();
  isLoadingUsers = false;
  isOnlyPendingUsersSelected: boolean;

  @Input({ required: true }) getUsersAction: (filterCriteria: FilterCriteria) => Observable<PaginatedResponse<UserWithRolesResponse>>;
  @Input({ required: true }) roles$: Observable<RoleResponse[]>;
  @Input() reinvitePendingUsersAction: (model: ReinvitePendingUsersModel) => Observable<void>;
  @Input() deleteUsersAction: (model: DeleteUsersRequest) => Observable<UsersDeletedResponse>;
  @Input() bulkUpdateUsersAction: (file: File) => Observable<UpdateBulkUsersResponse>;
  @Input() inviteUsersAction: (model: InviteUsersRequest[]) => Observable<UserWithRolesResponse[]>;
  @Input() bulkInviteUsersAction: (file: File) => Observable<InviteBulkUsersResponse>;
  @Input() updateUserAction: (userId: number, model: UpdateUserRequest) => Observable<void>;

  filterCriteria$: BehaviorSubject<FilterCriteria>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private notificationService: NzNotificationService
  ) {}

  ngOnInit(): void {
    this.initializeFilterCriteria();
  }

  ngOnDestroy(): void {
    this.filterCriteria$.unsubscribe();
  }

  onUserChecked({ checked, user }: CheckUserEvent): void {
    if (checked) {
      this.selectedUsers.add(user);
    } else {
      this.selectedUsers.delete(user);
    }
  }

  onUserInvited() {
    this.fetchUsers();
  }

  private initializeFilterCriteria(): void {
    this.filterCriteria$ = new BehaviorSubject<FilterCriteria>({
      names: this.queryParamAsArray('names'),
      roles: this.queryParamAsArray('roles'),
      statuses: this.queryParamAsArray('statuses').length == 0 ? ['Active', 'Pending'] : this.queryParamAsArray('statuses'),
      page: this.queryParamAsInt('page', 1),
      pageSize: this.queryParamAsInt('pageSize', 10),
    });

    this.filterCriteria$.subscribe((filterCriteria) => {
      this.updateUrl(filterCriteria);
      this.fetchUsers(filterCriteria);
    });
  }

  private queryParamAsArray(paramName: string): string[] {
    return this.route.snapshot.queryParams[paramName]
      ? Array.isArray(this.route.snapshot.queryParams[paramName])
        ? this.route.snapshot.queryParams[paramName]
        : [this.route.snapshot.queryParams[paramName]]
      : [];
  }

  private queryParamAsInt(paramName: string, defaultValue: number): number {
    if (isNaN(this.route.snapshot.queryParams[paramName])) {
      return defaultValue;
    }
    return +this.route.snapshot.queryParams[paramName];
  }

  private updateUrl(filterCriteria: FilterCriteria): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: filterCriteria,
      queryParamsHandling: 'merge',
    });
  }

  private fetchUsers(filterCriteria: FilterCriteria | null = null): void {
    const filter = filterCriteria ?? this.filterCriteria$.value;
    this.isLoadingUsers = true;
    this.selectedUsers.clear();
    this.getUsersAction(filter).subscribe({
      next: (users) => {
        this.paginatedUsersResponse = users;
        this.isLoadingUsers = false;
      },
      error: () => {
        this.isLoadingUsers = false;
      },
    });
  }

  onUserUpdated() {
    this.notificationService.success(
      this.translateService.instant('Portal.InspectionCompanies.Users.Update.Success.Title'),
      this.translateService.instant('Portal.InspectionCompanies.Users.Update.Success.Message')
    );
    this.fetchUsers();
  }

  onUserDeleted() {
    this.notificationService.success(
      this.translateService.instant('Portal.InspectionCompanies.Users.Delete.Success.Title'),
      this.translateService.instant('Portal.InspectionCompanies.Users.Delete.Success.Message')
    );
    this.fetchUsers();
  }

  onUsersDeleted(reloadNeeded: boolean) {
    this.selectedUsers.clear();
    if (reloadNeeded) {
      this.fetchUsers();
    }
  }

  onUsersReinvited() {
    this.notificationService.success(
      this.translateService.instant('Portal.InspectionCompanies.Users.ResendInvitations.Success.Title'),
      this.translateService.instant('Portal.InspectionCompanies.Users.ResendInvitations.Success.Message')
    );
    this.fetchUsers();
  }

  onBulkUsersUpdated() {
    this.fetchUsers();
  }
}

export interface CheckUserEvent {
  checked: boolean;
  user: UserWithRolesResponse;
}
